/**
 * Library API – DB 기반 Library 목록 및 GCS 동기화
 * GET /api/library/items, POST /api/library/sync-from-gcs, DELETE /api/library/items/:id
 */

import { apiCall } from "./base";

export interface LibraryConversionCheckResponse {
  source_conversion_job_id: string | null;
  quality_state: "no_issues" | "needs_review" | "not_checked" | "failed";
  converter_outcome: "success" | "warning" | "failed";
  loader_outcome: "not_checked" | "success" | "failed";
  original_size_bytes: number | null;
  vse_size_bytes: number | null;
  object_size: {
    width: number | null;
    height: number | null;
    depth: number | null;
    unit: string | null;
  };
  materials: {
    state: "no_issues" | "needs_review" | "not_checked";
    total: number | null;
    replaced_count: number;
    may_differ: boolean;
  };
  textures: {
    state: "no_issues" | "needs_review" | "not_checked";
    total: number | null;
    missing_count: number;
  };
  warning_codes: string[];
}

/** 백엔드 library_objects 한 건 응답 (목록/상세 공통) */
export interface LibraryItemResponse {
  id: string;
  display_name: string;
  gcs_path: string;
  size_bytes: number | null;
  status: string | null;
  source_format: string | null;
  created_at: string | null;
  last_used_at: string | null;
  updated_at: string | null;
  conversion_job_id: string | null;
  conversion_check?: LibraryConversionCheckResponse | null;
  /** 목록 API에서만 반환. 이 VSE를 사용하는 ViewR 프로젝트 수 */
  used_in_count?: number;
}

/** GET /api/library/items 응답 (배열) */
export type GetLibraryItemsResponse = LibraryItemResponse[];

/** POST /api/library/sync-from-gcs 또는 sync-all 응답 */
export interface SyncLibraryFromGcsResponse {
  synced: number;
  /** GCS에서 없어진 항목으로 DB에서 제거된 개수 (동기화 완료 토스트용) */
  deleted?: number;
  message: string;
  items_count: number;
}

/** DELETE /api/library/items/:id 응답 */
export interface DeleteLibraryItemResponse {
  message: string;
  id: string;
}

export type BulkFailureReason =
  | "not_found"
  | "forbidden"
  | "invalid_id"
  | "already_in_trash"
  | "internal_error";

export interface BulkItemFailure {
  id: string;
  reason: BulkFailureReason;
}

export interface BulkDeleteResponse {
  deleted_ids: string[];
  failed: BulkItemFailure[];
}

/** Used in 한 건: ViewR 파일 경로 + Space(프로젝트) 이름 */
export interface UsedInEntry {
  viewr_gcs_path: string;
  display_name: string;
  /** 사용하는 Space(ViewR 프로젝트) 이름 */
  space_name?: string;
}

/** GET /api/library/items/:id 상세 응답 (used_in 포함) */
export interface LibraryItemDetailResponse {
  id: string;
  display_name: string;
  gcs_path: string;
  size_bytes: number | null;
  status: string | null;
  source_format: string | null;
  created_at: string | null;
  last_used_at: string | null;
  updated_at: string | null;
  conversion_job_id: string | null;
  conversion_check?: LibraryConversionCheckResponse | null;
  /** 이 VSE를 사용하는 ViewR 파일 개수 */
  used_in_count: number;
  /** 사용하는 Space 이름 리스트 */
  used_in: UsedInEntry[];
}

/**
 * Library 목록 조회 (DB library_objects)
 */
export const getLibraryItems = async (
  userToken: string | null
): Promise<GetLibraryItemsResponse> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  const data = (await apiCall("/api/library/items", options)) as GetLibraryItemsResponse;
  return Array.isArray(data) ? data : [];
};

/**
 * Library 항목 상세 조회 (GET /api/library/items/:id)
 * Name, Status, Source format, Date uploaded, Last used, Used in 포함
 */
export const getLibraryItemDetail = async (
  userToken: string | null,
  itemId: string
): Promise<LibraryItemDetailResponse> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  return (await apiCall(
    `/api/library/items/${encodeURIComponent(itemId)}`,
    options
  )) as LibraryItemDetailResponse;
};

/**
 * GCS convert 버킷 목록을 DB에 동기화 (없는 항목만 INSERT)
 */
export const syncLibraryFromGcs = async (
  userToken: string | null
): Promise<SyncLibraryFromGcsResponse> => {
  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  return (await apiCall("/api/library/sync-from-gcs", options)) as SyncLibraryFromGcsResponse;
};

/**
 * VSE + ViewR 한 번에 GCS와 DB 동기화 (POST /api/library/sync-all)
 */
export const syncAllFromGcs = async (
  userToken: string | null
): Promise<SyncLibraryFromGcsResponse> => {
  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  return (await apiCall("/api/library/sync-all", options)) as SyncLibraryFromGcsResponse;
};

/**
 * Library 항목 삭제 (GCS + DB)
 */
export const deleteLibraryItem = async (
  userToken: string | null,
  itemId: string
): Promise<DeleteLibraryItemResponse> => {
  const options: RequestInit = {
    method: "DELETE",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  return (await apiCall(
    `/api/library/items/${encodeURIComponent(itemId)}`,
    options
  )) as DeleteLibraryItemResponse;
};

export const bulkDeleteLibraryItems = async (
  userToken: string | null,
  ids: string[],
): Promise<BulkDeleteResponse> => {
  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ids }),
  };
  return (await apiCall(
    "/api/library/items/bulk-delete",
    options,
  )) as BulkDeleteResponse;
};

/** PATCH /api/library/items/:id 응답 */
export interface RenameLibraryItemResponse {
  status: string;
  item?: LibraryItemResponse;
}

/**
 * Library 항목 표시명 수정 (PATCH /api/library/items/:id)
 * GCS 경로는 변경하지 않고 DB display_name만 수정
 */
export const renameLibraryItem = async (
  userToken: string | null,
  itemId: string,
  displayName: string
): Promise<RenameLibraryItemResponse> => {
  const options: RequestInit = {
    method: "PATCH",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ display_name: displayName }),
  };
  return (await apiCall(
    `/api/library/items/${encodeURIComponent(itemId)}`,
    options
  )) as RenameLibraryItemResponse;
};
